import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormGroup,
    Checkbox,
    Typography,
    Box,
    Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { colors } from "./Theme/WebTheme";

const AdminPermissionDialog = ({
    open,
    onClose,
    onSave,
    editingUser = null,
}) => {
    const { t } = useTranslation();
    const addonsdata = useSelector((state) => state.addondata);

    // Check if sub-admin controls addon is enabled
    const isSubAdminControlsEnabled =
        addonsdata.data && addonsdata.data.sub_admin_controls;

    const [permissionType, setPermissionType] = useState("all");
    const [specificPermissions, setSpecificPermissions] = useState({
        dashboard: false,
        bookings: false,
        addbookings: false,
        users: false,
        pricing: false,
        cars: false,
        promos: false,
        notifications: false,
        cancelreasons: false,
        addtowallet: false,
        withdraws: false,
        allreports: false,
        complain: false,
        roles: false,
        admin_support_list: false,
        subscriptions: false,
        documentmanagement: false,
        settings: false,
        userwallet: false,
    });

    // Available pages for permission control (based on actual routes in App.js)
    const availablePages = [
        {
            key: "dashboard",
            label: t("dashboard_text") || "Dashboard",
            url: "/dashboard",
        },
        {
            key: "bookings",
            label: t("booking_history") || "Booking History",
            url: "/bookings",
        },
        {
            key: "addbookings",
            label: t("addbookinglable") || "Add Bookings",
            url: "/addbookings",
        },
        {
            key: "users",
            label: t("users") || "Users",
            url: "/users",
        },
        {
            key: "pricing",
            label: t("pricing") || "Pricing",
            url: "/pricing",
        },
        {
            key: "cars",
            label: t("cars") || "Cars",
            url: "/cars",
        },
        {
            key: "promos",
            label: t("promo") || "Promotions",
            url: "/promos",
        },
        {
            key: "notifications",
            label: t("push_notification_title") || "Notifications",
            url: "/notifications",
        },
        {
            key: "cancelreasons",
            label: t("cancellation_reasons") || "Cancellation Reasons",
            url: "/cancelreasons",
        },
        {
            key: "addtowallet",
            label: t("add_to_wallet") || "Add To Wallet",
            url: "/addtowallet",
        },
        {
            key: "withdraws",
            label: t("withdraws") || "Withdraws",
            url: "/withdraws",
        },
        {
            key: "allreports",
            label: t("report") || "All Reports",
            url: "/allreports",
        },
        {
            key: "complain",
            label: t("complain") || "Complaints",
            url: "/complain",
        },
        {
            key: "roles",
            label: t("roles_management") || "Roles Management",
            url: "/roles",
        },
        {
            key: "admin_support_list",
            label: t("admin_support") || "Admin Support",
            url: "/admin-support-list",
        },
        {
            key: "subscriptions",
            label: t("subscription") || "Subscriptions",
            url: "/subscriptions",
        },
        {
            key: "documentmanagement",
            label: t("document_management") || "Document Management",
            url: "/documentmanagement",
        },
        {
            key: "settings",
            label: t("settings") || "Settings",
            url: "/settings",
        },
        {
            key: "userwallet",
            label: t("my_wallet_tile") || "User Wallet",
            url: "/userwallet",
        },
    ];

    // Initialize permissions when editing user
    useEffect(() => {
        if (editingUser && editingUser.subAdminPermissions) {
            if (editingUser.subAdminPermissions.type === "all") {
                setPermissionType("all");
            } else {
                setPermissionType("specific");
                setSpecificPermissions(
                    editingUser.subAdminPermissions.pages || {}
                );
            }
        } else {
            // Default to all permissions for new users
            setPermissionType("all");
            setSpecificPermissions({
                dashboard: false,
                bookings: false,
                addbookings: false,
                users: false,
                pricing: false,
                cars: false,
                promos: false,
                notifications: false,
                cancelreasons: false,
                addtowallet: false,
                withdraws: false,
                allreports: false,
                complain: false,
                roles: false,
                admin_support_list: false,
                subscriptions: false,
                documentmanagement: false,
                settings: false,
                userwallet: false,
            });
        }
    }, [editingUser]);

    const handlePermissionTypeChange = (event) => {
        setPermissionType(event.target.value);
    };

    const handleSpecificPermissionChange = (pageKey) => {
        setSpecificPermissions((prev) => ({
            ...prev,
            [pageKey]: !prev[pageKey],
        }));
    };

    const handleSave = () => {
        const permissions = {
            type: permissionType,
            pages: permissionType === "specific" ? specificPermissions : null,
        };

        onSave(permissions);
        onClose();
    };

    const handleClose = () => {
        onClose();
    };

    // If sub-admin controls addon is not enabled, don't show the dialog
    if (!isSubAdminControlsEnabled) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: "8px",
                    boxShadow: "4px 4px 6px #9E9E9E",
                },
            }}
        >
            <DialogTitle
                style={{
                    backgroundColor: colors.MAIN_COLOR,
                    color: colors.BLACK,
                }}
            >
                {t("page_access_controls") || "Page Access Controls"}
            </DialogTitle>

            <DialogContent style={{ padding: "20px" }}>
                <Box mb={2}>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                    >
                        {t("sub_admin_controls_description") ||
                            "Configure which pages this administrator can access."}
                    </Typography>
                </Box>

                <FormControl component="fieldset" fullWidth>
                    <FormLabel
                        component="legend"
                        style={{ marginBottom: "10px", fontWeight: "bold" }}
                    >
                        {t("permission_type") || "Permission Type"}
                    </FormLabel>

                    <RadioGroup
                        value={permissionType}
                        onChange={handlePermissionTypeChange}
                        style={{ marginBottom: "20px" }}
                    >
                        <FormControlLabel
                            value="all"
                            control={<Radio color="primary" />}
                            label={
                                t("allow_all_controls") || "Allow All Controls"
                            }
                        />
                        <FormControlLabel
                            value="specific"
                            control={<Radio color="primary" />}
                            label={
                                t("select_specific_pages") ||
                                "Select Specific Pages"
                            }
                        />
                    </RadioGroup>
                </FormControl>

                {permissionType === "specific" && (
                    <>
                        <Divider style={{ margin: "20px 0" }} />
                        <FormControl component="fieldset" fullWidth>
                            <FormLabel
                                component="legend"
                                style={{
                                    marginBottom: "15px",
                                    fontWeight: "bold",
                                }}
                            >
                                {t("available_pages") || "Available Pages"}
                            </FormLabel>

                            <FormGroup>
                                {availablePages.map((page) => (
                                    <FormControlLabel
                                        key={page.key}
                                        control={
                                            <Checkbox
                                                checked={
                                                    specificPermissions[
                                                        page.key
                                                    ] || false
                                                }
                                                onChange={() =>
                                                    handleSpecificPermissionChange(
                                                        page.key
                                                    )
                                                }
                                                color="primary"
                                            />
                                        }
                                        label={page.label}
                                        style={{ marginBottom: "8px" }}
                                    />
                                ))}
                            </FormGroup>
                        </FormControl>
                    </>
                )}
            </DialogContent>

            <DialogActions
                style={{ padding: "20px", backgroundColor: "#f5f5f5" }}
            >
                <Button onClick={handleClose} color="secondary">
                    {t("cancel") || "Cancel"}
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    style={{
                        backgroundColor: colors.MAIN_COLOR,
                        color: colors.BLACK,
                        marginLeft: "10px",
                    }}
                >
                    {t("save") || "Save"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AdminPermissionDialog;
